import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  Activity,
  BreakKey,
  BreakMessage,
  createBreakId,
  DeclinedUser,
  FirestoreCollection,
  HubblFilter,
  HubblTask,
  HubblMessage,
  User,
  createInitialSystemMessage,
} from 'hubbl-shared';
import {HubblBreakDTO} from '../models';
import {firestore} from './firebase';
import {mapUserToUserDTO} from './utils/map-incoming-users/mapUserToUserDTO';

interface CreateBreakRequest {
  activities: Activity[];
  startDateTime: string;
  endDateTime?: string;
  isRepeatEnabled: boolean;
  invitedUsers: User[];
  filters: HubblFilter[];
  officeId?: string;
  messages: HubblMessage[];
  spaceId?: string;
  userCreator: User;
  tasks: HubblTask[];
}

const getPendingBreaks = async (
  companyId: string,
  userId: string,
): Promise<HubblBreakDTO[]> => {
  const breakRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
  );

  let breakQuery = query(
    breakRef,
    where(`${BreakKey.UserCreator}.id`, 'not-in', [userId]),
    where(`${BreakKey.UserAcceptor}`, '==', null),
  );

  const snapshot = await getDocs(breakQuery);
  const breaks = snapshot.docs.map(doc => doc.data() as HubblBreakDTO);

  return breaks;
};

const getScheduledBreaks = async (
  companyId: string,
  userId: string,
): Promise<HubblBreakDTO[]> => {
  const breakRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
  );

  let breakQuery = query(breakRef, where('userIds', 'array-contains', userId));

  const snapshot = await getDocs(breakQuery);
  const breaks = snapshot.docs.map(doc => doc.data() as HubblBreakDTO);

  return breaks;
};

const createBreak = async (
  companyId: string,
  breakData: CreateBreakRequest,
): Promise<HubblBreakDTO> => {
  const breakRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
  );

  const createdNowDate = new Date();
  const breakId = createBreakId({
    breakCreated: createdNowDate,
    userId: breakData.userCreator.id,
  });

  const breakItem: HubblBreakDTO = {
    id: breakId,
    ...breakData,
    startDateTime: Timestamp.fromDate(new Date(breakData.startDateTime)),
    endDateTime: breakData.endDateTime
      ? Timestamp.fromDate(new Date(breakData.endDateTime))
      : null,
    [BreakKey.UserCreator]: mapUserToUserDTO(breakData.userCreator),
    userAcceptor: null,
    invitedUsers: breakData.invitedUsers.map(user => ({
      id: user.id,
      fullName: user.fullName,
      firstName: user.firstName,
      photoUrl: user.photoUrl,
      department: user.department,
      email: user.email,
    })),
    filters: breakData.filters || [],
    isBreakPassed: false,
    messages: breakData.messages || [],
    userIds: [breakData.userCreator.id],
    declinedUserIds: [],
    declinedUsers: [],
    created: Timestamp.fromDate(createdNowDate),
    companyId,
    isRepeatedHubbl: false,
    officeId: breakData.userCreator.settings?.currentOfficeId || 'main',
    spaceId: breakData.userCreator.settings?.currentSpaceId || 'main',
    tasks: breakData.tasks || [],
  };

  const breakDocRef = doc(breakRef, breakId);
  await setDoc(breakDocRef, breakItem);

  return breakItem;
};

const acceptBreak = async (
  companyId: string,
  breakId: string,
  user: User,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  const userDto = mapUserToUserDTO(user);

  await runTransaction(firestore, async transaction => {
    const breakSnap = await transaction.get(breakRef);
    if (!breakSnap.exists()) {
      throw new Error('Break not found');
    }

    const breakData = breakSnap.data() as HubblBreakDTO;

    // Filter out the user from declined arrays
    const updatedDeclinedUserIds =
      breakData.declinedUserIds?.filter(id => id !== user.id) || [];
    const updatedDeclinedUsers =
      breakData.declinedUsers?.filter(
        declinedUser => declinedUser.user.id !== user.id,
      ) || [];
    const matchedDateTime = Timestamp.fromDate(new Date());
    const initialSystemMessage = createInitialSystemMessage();

    // Add user to userIds if not already present
    const updatedUserIds = breakData.userIds?.includes(user.id)
      ? breakData.userIds
      : [...(breakData.userIds || []), user.id];

    transaction.update(breakRef, {
      userAcceptor: userDto,
      userIds: updatedUserIds,
      declinedUserIds: updatedDeclinedUserIds,
      declinedUsers: updatedDeclinedUsers,
      matchedDateTime: matchedDateTime,
      messages: arrayUnion(initialSystemMessage),
    });
  });
};

const declineBreak = async (
  companyId: string,
  breakId: string,
  user: User,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  const declinedUser: DeclinedUser = {
    declinedAt: new Date().toISOString(),
    user: {
      id: user.id,
      fullName: user.fullName,
      firstName: user.firstName,
      photoUrl: user.photoUrl,
      email: user.email,
      department: user.department,
    },
  };

  await updateDoc(breakRef, {
    declinedUserIds: arrayUnion(user.id),
    declinedUsers: arrayUnion(declinedUser),
  });
};

const undeclineBreak = async (
  companyId: string,
  breakId: string,
  user: User,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  await runTransaction(firestore, async transaction => {
    const breakSnap = await transaction.get(breakRef);
    if (!breakSnap.exists()) {
      throw new Error('Break not found');
    }

    const breakData = breakSnap.data() as HubblBreakDTO;

    // Remove user from declined arrays
    const updatedDeclinedUserIds =
      breakData.declinedUserIds?.filter(id => id !== user.id) || [];
    const updatedDeclinedUsers =
      breakData.declinedUsers?.filter(
        declinedUser => declinedUser.user.id !== user.id,
      ) || [];

    transaction.update(breakRef, {
      declinedUserIds: updatedDeclinedUserIds,
      declinedUsers: updatedDeclinedUsers,
    });
  });
};

const deleteBreak = async (
  companyId: string,
  breakId: string,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  await deleteDoc(breakRef);
};

const getBreak = async (
  companyId: string,
  breakId: string,
): Promise<HubblBreakDTO | null> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  const snapshot = await getDoc(breakRef);

  if (snapshot.exists()) {
    return snapshot.data() as HubblBreakDTO;
  }

  return null;
};

const addBreakMessage = async (
  companyId: string,
  breakId: string,
  message: BreakMessage,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  await updateDoc(breakRef, {
    messages: arrayUnion(message),
  });
};

const onBreakMessagesChangeListener = (
  companyId: string,
  breakId: string,
  callback: (messages: BreakMessage[]) => void,
) => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  return onSnapshot(breakRef, snapshot => {
    if (snapshot.exists()) {
      const breakData = snapshot.data() as HubblBreakDTO;
      callback(breakData.messages || []);
    }
  });
};

// Task management functions for breaks
const addBreakTask = async (
  companyId: string,
  breakId: string,
  taskTitle: string,
  creatorId: string,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  const newTask: HubblTask = {
    id: createBreakId({
      breakCreated: new Date(),
      userId: creatorId,
    }),
    title: taskTitle,
    assignedUserId: null,
    createdDateTime: new Date().toISOString(),
    assignedDateTime: null,
  };

  await updateDoc(breakRef, {
    tasks: arrayUnion(newTask),
  });
};

const updateBreakTask = async (
  companyId: string,
  breakId: string,
  taskId: string,
  updates: Partial<HubblTask>,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  await runTransaction(firestore, async transaction => {
    const breakSnap = await transaction.get(breakRef);
    if (!breakSnap.exists()) {
      throw new Error('Break not found');
    }

    const breakData = breakSnap.data();
    const tasks = breakData.tasks || [];
    const taskIndex = tasks.findIndex((task: HubblTask) => task.id === taskId);

    if (taskIndex === -1) {
      throw new Error('Task not found');
    }

    const updatedTask = {
      ...tasks[taskIndex],
      ...updates,
    };

    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = updatedTask;

    transaction.update(breakRef, {
      tasks: updatedTasks,
    });
  });
};

const deleteBreakTask = async (
  companyId: string,
  breakId: string,
  taskId: string,
): Promise<void> => {
  const breakRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Breaks,
    breakId,
  );

  await runTransaction(firestore, async transaction => {
    const breakSnap = await transaction.get(breakRef);
    if (!breakSnap.exists()) {
      throw new Error('Break not found');
    }

    const breakData = breakSnap.data();
    const tasks = breakData.tasks || [];
    const updatedTasks = tasks.filter((task: HubblTask) => task.id !== taskId);

    transaction.update(breakRef, {
      tasks: updatedTasks,
    });
  });
};

export const breaks = {
  getPendingBreaks,
  getScheduledBreaks,
  createBreak,
  acceptBreak,
  declineBreak,
  undeclineBreak,
  deleteBreak,
  getBreak,
  addBreakMessage,
  onBreakMessagesChangeListener,
  addBreakTask,
  updateBreakTask,
  deleteBreakTask,
};
